<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { useField, useForm } from 'vee-validate'
import { object, array, date, string } from 'yup'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import {
  DCard,
  DBtn,
  DTextField,
  DSelectFieldSearch,
  DMultiselect,
  DModal,
  DLoading,
  DEmployeesFilter,
  FilterLinesIcon,
  Users01Icon
} from 'front-library'
import {
  convertArrayToString,
  ReportComparative,
  generateComparative,
  getPayroll,
  getProcessCountry,
  getEmployeesReport,
  getReportRequest,
  getProcessedPayrollsComparative,
  getConceptCategories,
  reduceEmployees
} from '@/app/core/services/reports'
usePayrollChange(() => back())

/* store, router, notifications */
const storePayroll = usePayrollStore()
const store = useUserStore()
const router = useRouter()
const route = useRoute()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
    ))
})

/* Datos */
const itemsPayrolls = ref<any[]>([])
const itemsEmployees = ref<string[]>([])
const itemsProcesses = ref<string[]>([])
const itemsLanguage = ref([
  { name: t('reports.language.spanish'), id: 'es' },
  { name: t('reports.language.english'), id: 'en' }
])
const searchLanguage = ref<string>('')
const searchProcess = ref<string>('')
const itemsCategories = ref<[]>([])
const urlFile = ref(null)
const refresh = ref()

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  payrolls: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  process: string().required(t('form.valid.required')),
  startPeriod: date().required(t('form.valid.required')),
  endPeriod: date().when('startPeriod', (startPeriod: any, schema) => {
    if (startPeriod[0] !== undefined) {
      return schema
        .min(
          startPeriod,
          `${t('form.valid.startDateMin')} ${dayjs(startPeriod).format(
            'DD/MM/YYYY'
          )}`
        )
        .required(t('form.valid.required'))
    }
    return schema.required(t('form.valid.required'))
  }),
  categories: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  employees: array()
    .min(1, t('form.valid.employeeSelectionRequired'))
    .required(t('form.valid.fieldRequired', { field: t('fields.employees') }))
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<any>({
  initialValues: { translate: 'es' },
  validationSchema
})

/* Definicion campos del formulario */
const { value: payrolls } = useField<string[]>(ref('payrolls'))
const { value: process } = useField<string>(ref('process'))
const { value: startPeriod } = useField<string | undefined>('startPeriod')
const { value: endPeriod } = useField<string | undefined>('endPeriod')
const { value: categories } = useField<string[]>(ref('categories'))
const { value: translate } = useField<string>(ref('translate'))
const { value: employees } = useField<any[]>(ref('employees'))
const { value: format } = useField(ref('format'))

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: any) => {
  const path = route.params.code as string
  const decodeCode = atob(path)
  const createReport: ReportComparative = {
    code: decodeCode,
    parameters: values
  }
  void generateReport(createReport)
})

/* Crear solicitud del reporte */
const generateReport = async (values: any) => {
  loading.value = true
  messageLoading.value = t('loading.report')
  try {
    const response = await generateComparative(values)
    reportRequest(response)
  } catch (error: any) {
    showError(error)
  }
}

/* Mostrar estado del la solicitud del reporte generado */
const reportRequest = (id: string) => {
  urlFile.value = null
  refresh.value = setInterval(async () => {
    try {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        if (response.errors.length === 1) {
          descriptionModal.value = response.errors[0]
        } else {
          for (let i = 0; i < response.errors.length; i++) {
            const error = response.errors[i]
            descriptionModal.value += error
            if (i < response.errors.length - 1) {
              descriptionModal.value += '<br>'
            }
          }
        }
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    } catch (error: any) {
      showError(error)
    }
  }, 5000)
}

/* Listar nóminas procesadas */
const getListProcessedPayroll = async () => {
  if (startPeriod.value === '') startPeriod.value = undefined
  if (endPeriod.value === '') endPeriod.value = undefined
  itemsEmployees.value = []
  let _dataStartPeriod = []
  let _employeesStartPeriod: string[] = []
  let _employeesEndPeriod: string[] = []
  let _dataClosedEndPeriod = []
  let _dataPrenominaEndPeriod = []
  let _itemsAllEmployees: any = []
  let _employees: any = []
  employees.value = []
  try {
    if (
      process.value != undefined &&
      startPeriod.value != undefined &&
      endPeriod.value != undefined &&
      payrolls.value?.length > 0
    ) {
      loading.value = true
      messageLoading.value = t('paymentReceipts.loading.payroll')
      let strPayrolls = await convertArrayToString(payrolls.value)
      if (startPeriod.value) {
        _dataStartPeriod = await getProcessedPayrollsComparative(
          strPayrolls,
          process.value,
          startPeriod.value,
          true
        )
        _employeesStartPeriod = validateProcessedPayroll(_dataStartPeriod)
      }
      if (endPeriod.value) {
        _dataClosedEndPeriod = await getProcessedPayrollsComparative(
          strPayrolls,
          process.value,
          endPeriod.value,
          true
        )
        _employeesEndPeriod = validateProcessedPayroll(_dataClosedEndPeriod)
        _dataPrenominaEndPeriod = await getProcessedPayrollsComparative(
          strPayrolls,
          process.value,
          endPeriod.value,
          false
        )
        _employeesEndPeriod = _employeesEndPeriod.concat(
          validateProcessedPayroll(_dataPrenominaEndPeriod)
        )
      }
      _itemsAllEmployees = [..._employeesStartPeriod, ..._employeesEndPeriod]
      _employees = reduceEmployees(_itemsAllEmployees)
      const strEmployees = await convertArrayToString(_employees)
      loading.value = false
      getEmployees(strEmployees)
    }
  } catch (error: any) {
    showError(error)
  }
}

/* Validar nomina procesada */
const validateProcessedPayroll = (data: any) => {
  let _employees: string[] = []
  for (const payroll of payrolls.value) {
    let employees = data.filter((item: any) => item.payroll_id === payroll)
    if (employees.length > 0) {
      _employees = _employees.concat(employees[0].employees)
    }
  }
  return _employees
}

/* Listar empleados */
const getEmployees = async (employeesIds: string) => {
  loading.value = true
  messageLoading.value = t('loading.employee')
  try {
    if (employeesIds !== '') {
      itemsEmployees.value = await getEmployeesReport('', '', employeesIds)
      employees.value = itemsEmployees.value.map((_: any) => _.id)
    } else {
      notifications.addNotification({
        title: t('reports.notification.noEmployeesFound'),
        text: t('reports.notification.noEmployees'),
        type: 'warning',
        time: 10000
      })
    }
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

/* Mostrar notificación de error */
const showError = (error: any) => {
  loading.value = false
  notifications.addNotification({
    title: error.message,
    text: error.response?.data?.message || '',
    type: 'error',
    time: 10000
  })
}

/* Cerrar modal */
const closeModal = () => {
  showModal.value = false
}

/* Volver a la lista de reportes */
const back = () => {
  void router.push({ name: 'MainReports' })
}

onMounted(async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const [promisePayrolls, promiseProcess, promiseCategories] =
      await Promise.all([
        getPayroll(store.user?.id as string),
        getProcessCountry(),
        getConceptCategories()
      ])
    itemsPayrolls.value = promisePayrolls
    payrolls.value = itemsPayrolls.value
      .filter((_: any) => _.id === storePayroll.payroll?.id)
      .map((_: any) => _.id)
    itemsProcesses.value = promiseProcess
    itemsProcesses.value = itemsProcesses.value.filter((_: any) => _.id != 'L')
    itemsCategories.value = promiseCategories
    categories.value = itemsCategories.value
      .filter((_: any) => _.code === 'base' || _.code === 'payroll')
      .map((_: any) => _.code)
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <DModal
      :open="showModal"
      :typeAlert="typeAlertModal"
      :title="titleModal"
      :description="descriptionModal"
      @closeModal="closeModal">
      <template v-slot:description v-if="urlFile != null">
        <a
          class="underline tracking-widest text-blue-600"
          :href="urlFile"
          target="_blank"
          v-html="t('reports.notification.viewFile')" />
      </template>
    </DModal>
    <div class="bg-gray-50 sm:pb-5 lg:pb-12">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('reports.comparative')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 10rem)">
      <div class="flex flex-col gap-4">
        <DCard :footer="true">
          <div class="grid grid-cols-4">
            <div class="col-span-4 lg:col-span-3 sm:pb-5 lg:pb-0 px-3">
              <div class="grid grid-cols-6 gap-4">
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.payrolls')"
                    v-model="payrolls"
                    :headerTitle="t('reports.fields.payrolls')"
                    :required="true"
                    :options="itemsPayrolls"
                    :error="errors.payrolls"
                    @change="getListProcessedPayroll()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.process')"
                    v-model="process"
                    :required="true"
                    :options="filterOptions(itemsProcesses, searchProcess)"
                    @filter="searchProcess = $event"
                    :error="errors.process"
                    @change="getListProcessedPayroll" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="`${t('reports.fields.endDatePeriod')} 1`"
                    v-model="startPeriod"
                    :required="true"
                    :error="errors.startPeriod"
                    @change="getListProcessedPayroll" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="`${t('reports.fields.endDatePeriod')} 2`"
                    v-model="endPeriod"
                    :required="true"
                    :error="errors.endPeriod"
                    @change="getListProcessedPayroll" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.categories')"
                    v-model="categories"
                    :value-key="'code'"
                    :headerTitle="t('reports.fields.categories')"
                    :required="true"
                    :options="itemsCategories"
                    :error="errors.categories" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.language')"
                    v-model="translate"
                    :options="filterOptions(itemsLanguage, searchLanguage)"
                    @filter="searchLanguage = $event" />
                </div>
              </div>
            </div>
            <div
              class="flex flex-col col-span-4 lg:col-span-1 border-0 border-t-2 lg:border-l-4 p-2 lg:border-t-0">
              <div class="pl-4 pr-4 lg:pr-0 flex justify-between">
                <h3
                  class="text-md font-medium leading-6 text-gray-900"
                  v-html="t('fields.employees')" />
                <h3
                  class="text-md font-medium leading-6 text-gray-900 flex items-center">
                  <DEmployeesFilter
                    :items="itemsEmployees"
                    v-model="employees" />
                  <FilterLinesIcon class="w-8" />
                </h3>
              </div>
              <div
                class="flex justify-center items-center p-5 sm:gap-4 lg:gap-3 xl:gap-4">
                <Users01Icon class="w-20 h-20" />
                <h1 class="text-5xl font-medium leading-6 text-gray-900">
                  {{
                    `${
                      employees?.length === undefined ? 0 : employees?.length
                    }/${itemsEmployees?.length}`
                  }}
                </h1>
              </div>
              <div class="text-xs text-red-500 self-center">
                {{ errors.employees }}
              </div>
            </div>
          </div>
          <template v-slot:footer>
            <div class="flex justify-end gap-2">
              <DBtn
                type="button"
                color="secondary"
                @click="back"
                class="px-5 py-2 font-semibold"
                v-html="t('form.button.back')" />
              <DBtn
                color="excel"
                class="px-5 py-2 font-semibold"
                @click.prevent="
                  onSubmit(),
                    (format =
                      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
                "
                v-html="t('form.button.excel')" />
              <DBtn
                color="danger"
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit(), (format = 'application/pdf')"
                v-html="t('form.button.pdf')" />
            </div>
          </template>
        </DCard>
      </div>
    </form>
  </div>
</template>
